/**
 * Чакат одобрение — what waits for the administrator (#420): places a dispatcher or a driver
 * placed or changed, used at once and waiting to be approved, and drivers' reports that a spot
 * is wrong, which move nothing until they are.
 *
 * Each row says what changed and who did it, and *Show* puts the before and the after on the
 * page's map. Approving a place approves it as it now stands; rejecting undoes what waited —
 * a new place goes, a moved one goes back. Approving a report moves the place to where the
 * passenger got in.
 */

import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../../shell/formats';
import type { PlaceReview } from '../../places/api';
import { Button, Pill, Section } from '../../ui';
import { metresBetween, who, whatChanged } from './words';

export function PlaceReviewList({
  items,
  people,
  busy,
  onShow,
  onApprove,
  onReject,
}: {
  items: PlaceReview[];
  people: Readonly<Record<string, string>>;
  busy: boolean;
  onShow: (item: PlaceReview) => void;
  onApprove: (item: PlaceReview) => void;
  onReject: (item: PlaceReview) => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  if (items.length === 0) return null;

  return (
    <Section title={t('places.review.title', { count: items.length })} description={t('places.review.about')}>
      <ul className="m-0 p-0 list-none flex flex-col">
        {items.map((item) => {
          const first = item.changes[0];
          const report = item.kind === 'reported';

          return (
            <li
              key={item.changeId ?? item.place.id}
              className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5 border-b border-rule last:border-b-0"
            >
              <span className="flex flex-col gap-0.5 min-w-0">
                <span className="flex items-center gap-2 font-medium">
                  {item.place.line}
                  <Pill tone={report ? 'danger' : item.kind === 'added' ? 'brand' : 'warn'} soft>
                    {t(`places.review.${item.kind}`)}
                  </Pill>
                </span>
                <span className="text-secondary text-ink-2">
                  {report
                    ? item.suggested == null
                      ? t('places.review.reportNoSpot')
                      : t('places.review.reportSpot', {
                          metres: Math.round(metresBetween(item.place.stop ?? item.place.door, item.suggested.stop ?? item.suggested.door)),
                        })
                    : whatChanged(t, item.before, item.changes[item.changes.length - 1].after)}
                </span>
                <span className="text-secondary text-ink-3">
                  {item.changes.map((change) => who(t, change.by, change.byUserId, people)).filter((name, index, all) => all.indexOf(name) === index).join(', ')}
                  {' · '}
                  {formats.moment(first.at)}
                </span>
              </span>
              <span className="flex gap-2 shrink-0">
                <Button onClick={() => onShow(item)}>{t('places.review.show')}</Button>
                <Button disabled={busy} onClick={() => onReject(item)}>
                  {report ? t('places.review.keep') : t('places.review.reject')}
                </Button>
                <Button variant="primary" disabled={busy} onClick={() => onApprove(item)}>
                  {report
                    ? item.suggested == null
                      ? t('places.review.close')
                      : t('places.review.moveThere')
                    : t('places.review.approve')}
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
